import { allocate, type Grosze } from "./money.ts";
import { vatFromGross, type VatCode } from "./vat.ts";

export type Discount =
  | { kind: "percent"; value: number } // 0–100, może być ułamkowy (np. 12.5)
  | { kind: "amount"; value: Grosze };

export interface PricedLine {
  id: string;
  unitPrice: Grosze; // cena jednostkowa wariantu + modyfikatory
  qty: number; // liczba całkowita sztuk
  vatCode: VatCode;
  vatPercent: number | null;
  voided?: boolean;
  discount?: Discount | null;
}

export interface LineTotal {
  id: string;
  gross: Grosze; // przed rabatami
  lineDiscount: Grosze;
  orderDiscount: Grosze; // przypisana część rabatu na rachunek
  net: Grosze; // do zapłaty za pozycję (brutto po rabatach)
  vatCode: VatCode;
}

export interface OrderTotals {
  lines: LineTotal[];
  subtotal: Grosze;
  discountTotal: Grosze;
  total: Grosze;
  vat: { code: VatCode; gross: Grosze; vat: Grosze }[];
}

export function discountAmount(base: Grosze, d: Discount | null | undefined): Grosze {
  if (!d || base <= 0) return 0;
  const amount = d.kind === "percent" ? Math.round((base * d.value) / 100) : d.value;
  return Math.max(0, Math.min(base, amount));
}

export function computeTotals(lines: PricedLine[], orderDiscount?: Discount | null): OrderTotals {
  const active = lines.filter((l) => !l.voided && l.qty > 0);
  const afterLine = active.map((l) => {
    const gross = l.unitPrice * l.qty;
    const lineDiscount = discountAmount(gross, l.discount);
    return { l, gross, lineDiscount, base: gross - lineDiscount };
  });
  const baseSum = afterLine.reduce((s, x) => s + x.base, 0);
  const orderDisc = discountAmount(baseSum, orderDiscount);
  const shares = allocate(orderDisc, afterLine.map((x) => x.base));

  const totals: LineTotal[] = afterLine.map((x, i) => ({
    id: x.l.id,
    gross: x.gross,
    lineDiscount: x.lineDiscount,
    orderDiscount: shares[i]!,
    net: x.base - shares[i]!,
    vatCode: x.l.vatCode,
  }));

  const byVat = new Map<VatCode, { gross: Grosze; percent: number | null }>();
  for (const [i, t] of totals.entries()) {
    const cur = byVat.get(t.vatCode) ?? { gross: 0, percent: afterLine[i]!.l.vatPercent };
    cur.gross += t.net;
    byVat.set(t.vatCode, cur);
  }

  const subtotal = totals.reduce((s, t) => s + t.gross, 0);
  const total = totals.reduce((s, t) => s + t.net, 0);
  return {
    lines: totals,
    subtotal,
    discountTotal: subtotal - total,
    total,
    vat: [...byVat.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([code, v]) => ({ code, gross: v.gross, vat: vatFromGross(v.gross, v.percent) })),
  };
}

export interface PickedModifier {
  groupId: string;
  price: Grosze;
  qty: number;
}

/**
 * Dopłata za dodatki jednej sztuki. W grupie z `freeQty` pierwsze N sztuk jest w cenie —
 * za darmo idą najdroższe, żeby gość nigdy nie stracił na kolejności klikania.
 */
export function modifiersPrice(picks: PickedModifier[], freeQty: (groupId: string) => number): Grosze {
  const byGroup = new Map<string, Grosze[]>();
  for (const p of picks) {
    const units = byGroup.get(p.groupId) ?? [];
    for (let i = 0; i < p.qty; i++) units.push(p.price);
    byGroup.set(p.groupId, units);
  }
  let sum = 0;
  for (const [groupId, units] of byGroup) {
    units.sort((a, b) => b - a);
    sum += units.slice(freeQty(groupId)).reduce((s, x) => s + x, 0);
  }
  return sum;
}
